import React, { useState, useEffect, useMemo } from "react";
import { T, getStyles, applyTheme } from "../lib/theme";
import { fmt, num, requestFullAccess } from "../lib/utils";
import { resolveValue, resolveBestOffer, resolveInsuranceQuote, resolveRefiRate } from "../lib/valuation";
import { Spin } from "./Atoms";
import { VehicleModal } from "./VehicleModal";
import { VehicleRow } from "./VehicleRow";
import { ServiceModal } from "./ServiceModal";
import { ClydePanel } from "./ClydePanel";
import { AdminDashboard } from "./AdminDashboard";
import { AccountSettings } from "./AccountSettings";
import { DemoWalkthrough } from "./DemoWalkthrough";
import HomeFleetBuying from "../HomeFleet_BuyingFlow";

const PORTFOLIO_TABS = [
  { key: "insurance", label: "Insurance Savings" },
  { key: "refi", label: "Refi Savings" },
  { key: "offer", label: "Offer Value" },
];

export function HomeFleetApp({onSignOut}){
  const[vehicles,setVehicles]=useState([]);
  const[maintenance,setMaintenance]=useState([]);
  const[householdState,setHouseholdState]=useState(null); // 2-letter code or null — used for the EIA gas-price regional lookup
  const[householdZip,setHouseholdZip]=useState(null); // 5-digit code or null — used for the market-listings dealer/inventory search radius
  const[loading,setLoading]=useState(true);
  const[loadError,setLoadError]=useState("");
  const[saveError,setSaveError]=useState("");
  const[addVeh,setAddVeh]=useState(false);
  const[svcVehicle,setSvcVehicle]=useState(null);
  const[email,setEmail]=useState(null);
  const[fullName,setFullName]=useState(null); // users.full_name, nullable — feeds the Add Vehicle form's auto-generated nickname; VehicleModal falls back to a name-free default when this is null
  const[accessLevel,setAccessLevel]=useState(null);
  const[meLoaded,setMeLoaded]=useState(false);
  // Single mutually-exclusive view selector, replacing what used to be
  // separate showAdmin/showSettings booleans each manually closing the
  // other — one value can only ever be one thing, so a third view
  // (buying) doesn't need a third pairwise close-the-others wire-up.
  const[activeView,setActiveView]=useState(null); // "admin"|"settings"|"buying"|null
  // Lifted out of ClydePanel (a controlled open/onOpenChange pair now)
  // so it can be reopened from the Buy Center's own "General chat" icon,
  // not just its own floating button — ClydePanel is never unmounted
  // while activeView switches, only its floating trigger is hidden (see
  // hideTrigger below), so nothing about its own conversation state is
  // ever lost by navigating to or from Buy Center.
  const[clydeOpen,setClydeOpen]=useState(false);
  // Whatever Kate had extracted from the general chat at the moment
  // onBuying fired, if anything — handed straight through to
  // HomeFleetBuying as its pendingHandoff prop, which Clyde must recap
  // and get explicitly confirmed before doing anything with it (see
  // HomeFleet_BuyingFlow.jsx's own hard confirmation gate). null when
  // there's nothing to hand off yet (e.g. a direct "Shop" click that
  // never went through Kate at all) — HomeFleetBuying treats that as no
  // handoff pending, same as it always has.
  //
  // Cleared below the moment activeView leaves "buying" — without this,
  // a real handoff's data would silently outlive the visit it was made
  // for, and a LATER direct "Shop"/"Resume" click (bypassing Kate
  // entirely) would incorrectly make Clyde re-recap and demand
  // reconfirmation of stale, possibly outdated numbers from a prior
  // visit. Harmless before this stage (a stale seed just quietly
  // pre-filled the worksheet); a real, confusing bug once the
  // confirmation gate reacts to pendingHandoff's mere presence.
  const[buyingSeedIntent,setBuyingSeedIntent]=useState(null);
  useEffect(function(){
    if(activeView!=="buying")setBuyingSeedIntent(null);
  },[activeView]);
  const[showWalkthrough,setShowWalkthrough]=useState(null); // null = not yet decided
  const[showAddCapNote,setShowAddCapNote]=useState(false);
  const[showShopCapNote,setShowShopCapNote]=useState(false);
  const[goLiveSent,setGoLiveSent]=useState(false); // shared across every demo-upgrade nudge in this file (Add Vehicle's and Shop's) — one account-level "have we already asked" flag, not one per nudge
  // An open, Clyde-kind conversation with a linked deal, if one exists
  // for this user — set by the effect below.
  const[resumableDeal,setResumableDeal]=useState(null);
  // The household's active deal, as stored. deals.deal_data is what
  // extract_deal_intent has been accumulating, and until now nothing in
  // this app ever read it back -- which is why a reload asked for a
  // vehicle the household had already named and why a sent deal could not
  // be reopened. Owned here rather than in either panel because the
  // active deal is a household-level fact and both personas are children
  // of this component; two independent fetches would let Kate and Clyde
  // disagree about the household's own budget. See
  // HOMEFLEET_DEAL_PERSISTENCE_PLAN.md section 0.
  const[restoredIntent,setRestoredIntent]=useState(null);
  // display_prefs (migrations/0020) — one JSON blob for both the
  // light/dark choice and each vehicle's currently-open tab, loaded as
  // part of /api/me below and saved back via PATCH /api/me on every
  // change. Fire-and-forget on save, same convention as this file's
  // other background writes (the enrich call in addVehicle below) —
  // losing a preference write isn't worth surfacing a UI error for, but
  // it's still logged so a pattern of failures is visible somewhere.
  const[displayPrefs,setDisplayPrefs]=useState({});
  const savePrefs=next=>{
    setDisplayPrefs(next);
    fetch("/api/me",{method:"PATCH",headers:{"Content-Type":"application/json"},body:JSON.stringify({displayPrefs:next})})
      .catch(e=>console.error("Failed to save display preferences:",e.message||e));
  };
  // Toggling mutates T's own properties in place (theme.js's applyTheme)
  // and this state change just forces the re-render that makes every
  // component's next render pick those new values up; theme itself is
  // never read for its value elsewhere, only used to drive the toggle UI.
  const[theme,setTheme]=useState("dark");
  const changeTheme=next=>{applyTheme(next);setTheme(next);savePrefs({...displayPrefs,theme:next});};
  const handleTabChange=(vehicleId,tab)=>{savePrefs({...displayPrefs,openTab:{...displayPrefs.openTab,[vehicleId]:tab}});};
  // Reported up from each VehicleRow's own already-computed TCO (Decision
  // 3) — vehicle-local fuel/mpg fetching stays exactly where it was, only
  // the resolved number comes up. A vehicle whose Financial/Fuel tab was
  // never opened this session has no entry here yet; the Monthly Outlay
  // calc below falls back to payment+insurance for those so the portfolio
  // total is never misleadingly low, just less complete than after every
  // tab's been visited once.
  const[tcoByVehicle,setTcoByVehicle]=useState({});
  const handleTcoChange=(vehicleId,total)=>{
    if(total==null)return;
    setTcoByVehicle(p=>p[vehicleId]===total?p:{...p,[vehicleId]:total});
  };
  const[activePTab,setActivePTab]=useState(null);

  // Merges an enrich response's {field:{amount,source,origin,low,high,usage}}
  // directly into the matching vehicle's financials — no refetch. Matches
  // the shape GET /api/fleet already returns (functions/_lib/fleetData.js's
  // financialRowToJson), minus fetchedAt/usage which nothing here reads yet.
  const mergeFinancials=(vehicleId,results)=>{
    setVehicles(p=>p.map(v=>{
      if(v.id!==vehicleId)return v;
      const next={...v.financials};
      for(const[field,r] of Object.entries(results||{})){
        next[field]={amount:r.amount,source:r.source,origin:r.origin,low:r.low,high:r.high};
      }
      return{...v,financials:next};
    }));
  };

  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      try{
        const res=await fetch("/api/fleet");
        const body=await res.json().catch(()=>({}));
        if(!res.ok){
          const msg=body.error==="not authenticated"
            ?"Your session has expired — reload the page and enter the password again."
            :(body.error||"Could not load your fleet.");
          throw new Error(msg);
        }
        if(cancelled)return;
        setVehicles(body.vehicles||[]);
        setMaintenance(body.maintenance||[]);
        setHouseholdState(body.household?.state||null);
        setHouseholdZip(body.household?.zip||null);
      }catch(e){
        if(!cancelled)setLoadError(e.message||"Could not load your fleet.");
      }finally{
        if(!cancelled)setLoading(false);
      }
    })();
    return()=>{cancelled=true;};
  },[]);

  // Independent of the fleet load above — just for the header's "signed
  // in as" display. A failure here is low-stakes (email stays null, the
  // header just shows Sign out with no email) so it's not wired into
  // loadError at all.
  useEffect(()=>{
    let cancelled=false;
    fetch("/api/me").then(res=>res.json()).then(body=>{
      if(cancelled)return;
      setEmail(body.email||null);setFullName(body.fullName||null);setAccessLevel(body.accessLevel||null);
      const prefs=body.displayPrefs||{};
      setDisplayPrefs(prefs);
      // Dark is already T's own default (theme.js) and this state's
      // initial value above — only an explicit saved "light" choice
      // needs to actually change anything here. A saved "dark" (or no
      // saved preference at all) is a no-op, already matching what's
      // already showing.
      if(prefs.theme==="light"){applyTheme("light");setTheme("light");}
    }).catch(()=>{}).finally(()=>{if(!cancelled)setMeLoaded(true);});
    return()=>{cancelled=true;};
  },[]);

  // Looks for a resumable buying conversation once accessLevel is known
  // — skipped entirely for a demo account, same gate as every other
  // buying-related affordance in this file, rather than issuing a
  // network call that could only ever come back empty for that tier
  // anyway (a demo account can never reach the flow that would create
  // one). Low-stakes, matching the /api/me effect above: a failure here
  // just means no resume nudge shows, not a load error.
  // Same gate and same low-stakes failure behaviour as the conversations
  // effect below: a failure means no restore, not a load error.
  useEffect(()=>{
    if(!meLoaded||accessLevel==="demo")return;
    let cancelled=false;
    fetch("/api/deals").then(res=>res.ok?res.json():null).then(body=>{
      if(cancelled||!body||!body.activeDeal||!body.activeDeal.dealData)return;
      try{
        const d=JSON.parse(body.activeDeal.dealData);
        // A deal with no vehicle on it is not worth restoring -- the
        // worksheet keys off make/model and would render an empty shell.
        if(d&&d.make&&d.model)setRestoredIntent(d);
      }catch{/* unparseable deal_data restores nothing, same as none */}
    }).catch(()=>{});
    return()=>{cancelled=true;};
  },[meLoaded,accessLevel]);

  useEffect(()=>{
    if(!meLoaded||accessLevel==="demo")return;
    let cancelled=false;
    fetch("/api/conversations").then(res=>res.json()).then(body=>{
      if(cancelled)return;
      const open=(body.conversations||[]).find(c=>c.kind==="Clyde"&&c.status==="open"&&c.linkedDealId);
      setResumableDeal(open||null);
    }).catch(()=>{});
    return()=>{cancelled=true;};
  },[meLoaded,accessLevel]);

  // Decided once, the first time both loads have resolved — not
  // continuously re-derived from vehicles.length, since the walkthrough
  // itself adds a vehicle partway through (0 -> 1) and must keep
  // control of the screen through its own summary/soft-close steps
  // rather than being yanked away the instant that count changes. A
  // demo account that later deletes its only vehicle and reloads the
  // page will see the walkthrough again — acceptable, not a bug: an
  // empty demo fleet is exactly the state this flow exists to fill.
  useEffect(()=>{
    if(showWalkthrough===null&&!loading&&meLoaded){
      setShowWalkthrough(accessLevel==="demo"&&vehicles.length===0);
    }
  },[showWalkthrough,loading,meLoaded,accessLevel,vehicles.length]);

  const handleSignOut=async()=>{
    try{await fetch("/api/logout",{method:"POST"});}catch{}
    onSignOut();
  };

  // All three mutations return true/false so callers (VehicleRow's edit
  // modal, the add-vehicle modal below) know whether to close on their
  // own — a failed save should never look the same as a successful one.
  const addVehicle=async v=>{
    setSaveError("");
    try{
      const res=await fetch("/api/vehicles",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(v)});
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not save vehicle.");
      setVehicles(p=>[body,...p]);
      // Best-effort background enrichment — Live mode only (checked here,
      // not trusted server-side; the enrich endpoint enforces its own cap
      // regardless of what mode the client claims). The UI intentionally
      // stays quiet on failure — the manual Refresh button in VehicleRow
      // is the user-facing retry — but every failure is still logged so
      // a pattern of them is visible somewhere. fetch() only rejects on
      // network failure, not on a non-2xx response (the cap's 429, say),
      // so both cases are checked explicitly rather than relying on catch.
      if(dataMode==="live"){
        fetch(`/api/vehicles/${body.id}/enrich`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({})})
          .then(async r=>{
            const b=await r.json().catch(()=>({}));
            if(!r.ok){console.error(`Enrich failed for vehicle ${body.id}:`,b.error||r.status);return;}
            mergeFinancials(body.id,b.results);
          })
          .catch(e=>{console.error(`Enrich request failed for vehicle ${body.id}:`,e.message||e);});
      }
      return true;
    }catch(e){
      setSaveError(e.message||"Could not save vehicle.");
      return false;
    }
  };
  const updateVehicle=async(id,patch)=>{
    setSaveError("");
    try{
      const res=await fetch(`/api/vehicles/${id}`,{method:"PATCH",headers:{"Content-Type":"application/json"},body:JSON.stringify(patch)});
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not save changes.");
      setVehicles(p=>p.map(v=>v.id===id?body:v));
      return true;
    }catch(e){
      setSaveError(e.message||"Could not save changes.");
      return false;
    }
  };
  const deleteVehicle=async id=>{
    setSaveError("");
    try{
      const res=await fetch(`/api/vehicles/${id}`,{method:"DELETE"});
      const body=await res.json().catch(()=>({}));
      if(!res.ok)throw new Error(body.error||"Could not delete vehicle.");
      setVehicles(p=>p.filter(v=>v.id!==id));
      setMaintenance(p=>p.filter(m=>m.vehicleId!==id));
      return true;
    }catch(e){
      setSaveError(e.message||"Could not delete vehicle.");
      return false;
    }
  };
  const totals=useMemo(()=>{
    const resolved=vehicles.map(v=>({v,val:resolveValue(v)}));
    const value=resolved.reduce((s,{val})=>s+(val.amount||0),0),loan=vehicles.reduce((s,v)=>s+num(v.loan),0),equity=value-loan;
    const payments=vehicles.reduce((s,v)=>s+num(v.payment),0);
    // Monthly Outlay: fuel+maintenance+payment+insurance per vehicle
    // (Decision 3) where VehicleRow has reported it, else payment+
    // insurance only (data already on hand, no fetch) for a vehicle
    // whose Financial/Fuel tab hasn't been opened yet this session —
    // an honest partial total instead of undercounting those vehicles
    // as $0.
    const monthlyOutlay=vehicles.reduce((s,v)=>s+(tcoByVehicle[v.id]??(num(v.payment)+num(v.insurance))),0);
    const refiSave=vehicles.reduce((s,v)=>{if(!num(v.loan)||!num(v.rate))return s;return s+Math.max(0,Math.round((num(v.loan)*((num(v.rate)-resolveRefiRate(v).amount)/100))/12));},0);
    const insSave=vehicles.reduce((s,v)=>{const q=resolveInsuranceQuote(v);return q?s+Math.max(0,num(v.insurance)-q.amount):s;},0);
    // Sum of every vehicle's OWN best offer, for the Offer Value portfolio tab.
    const offerValue=resolved.reduce((s,{v,val})=>{const o=resolveBestOffer(v,val.amount);return s+(o?o.amount:0);},0);
    return{value,loan,equity,payments,monthlyOutlay,refiSave,insSave,offerValue};
  },[vehicles,tcoByVehicle]);
  // There is no more Demo/Live toggle — dataMode is a straight mirror of
  // the account's real access_level, decided server-side (see
  // functions/api/signup.js and functions/api/admin/review-signup.js),
  // not something the client can flip. Every callsite that used to read
  // dataMode off the old toggle state now reads this same derived value.
  const dataMode=(accessLevel==="live"||accessLevel==="admin")?"live":"demo";
  const isLive=dataMode==="live";
  // Session 2b: once a demo account has a real (non-example) vehicle,
  // it's used its one lifetime pull — the button stays visible (was
  // effectively invisible before, gated only by the pre-existing
  // dataMode toggle) but gets its own disabled state and message,
  // rather than reusing the unrelated "Switch to Live to add" copy
  // that only ever applied to the dataMode toggle. Deliberately not
  // `disabled` in the HTML sense — a real disabled button wouldn't
  // fire onClick at all, and the whole point is to show the nudge on
  // click instead of silently doing nothing.
  const demoVehicleCapped=accessLevel==="demo"&&vehicles.some(v=>!v.isExample);
  const addBtnDisabled=dataMode==="demo"&&!demoVehicleCapped;

  // Waits on the walkthrough decision too, not just the fleet/me
  // fetches — otherwise there'd be one render where a fresh demo
  // signup briefly sees the normal empty-fleet view before flipping
  // to the walkthrough a tick later.
  if(loading||!meLoaded||showWalkthrough===null)return(
    <div className="hf" style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <style>{getStyles(T)}</style>
      <div style={{display:"flex",alignItems:"center",gap:10,color:T.textMd,fontSize:14}}><Spin sz={20}/>Loading your fleet...</div>
    </div>
  );

  return(
    <div className="hf">
      <style>{getStyles(T)}</style>
      {/* position:fixed + a z-index above every modal in the app (highest
          in use is MaintenanceSchedule's 9999) — a plain in-flow banner
          would render behind an open modal's backdrop and be invisible,
          which would turn a real save failure into a silent one. */}
      {loadError&&<div style={{position:"fixed",top:0,left:0,right:0,zIndex:20000,background:T.redL,borderBottom:`1px solid ${T.redBorderStrong}`,padding:"10px 20px",textAlign:"center",fontSize:13,color:T.red,fontWeight:600}}>{loadError}</div>}
      {saveError&&<div style={{position:"fixed",top:0,left:0,right:0,zIndex:20000,background:T.redL,borderBottom:`1px solid ${T.redBorderStrong}`,padding:"10px 20px",textAlign:"center",fontSize:13,color:T.red,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",gap:10}}><span>{saveError}</span><button onClick={()=>setSaveError("")} style={{background:"none",border:"none",color:T.red,fontWeight:700,cursor:"pointer",fontSize:13}}>Dismiss</button></div>}
      <div style={{background:T.card,borderBottom:`1px solid ${T.border}`,position:"sticky",top:0,zIndex:50,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
        <div style={{maxWidth:980,margin:"0 auto",padding:"0 20px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",height:62,gap:12}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <img src="/logo192.png" alt="HomeFleet" style={{width:36,height:36,borderRadius:10,objectFit:"cover",flexShrink:0}}/>
              <div><div style={{fontSize:18,fontWeight:800,color:T.text,letterSpacing:"-.02em",lineHeight:1.1}}>HomeFleet</div><div style={{fontSize:11,color:T.textSm,fontWeight:500}}>Vehicle Finance Manager</div></div>
            </div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
              {!isLive&&<>
                <div style={{fontSize:11,fontWeight:700,color:T.amber,background:T.amberL,border:`1px solid ${T.amberBorderStrong}`,borderRadius:20,padding:"4px 12px"}}>Pending Review</div>
                <div style={{fontSize:10,color:T.amber,fontWeight:600}}>Limited features until reviewed</div>
              </>}
            </div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2,maxWidth:220}}>
              <button onClick={()=>{if(demoVehicleCapped)setShowAddCapNote(v=>!v);else if(!addBtnDisabled)setAddVeh(true);}} disabled={addBtnDisabled} style={{background:(addBtnDisabled||demoVehicleCapped)?T.btnDisabledBg:`linear-gradient(135deg,${T.teal},#0f766e)`,color:(addBtnDisabled||demoVehicleCapped)?T.textSm:"#fff",border:"none",borderRadius:9,padding:"9px 16px",fontSize:13,fontWeight:700,boxShadow:(addBtnDisabled||demoVehicleCapped)?"none":"0 2px 8px rgba(13,148,136,.3)",cursor:addBtnDisabled?"default":"pointer",opacity:(addBtnDisabled||demoVehicleCapped)?.7:1}}>+ Add Vehicle</button>
              {addBtnDisabled&&<div style={{fontSize:10,color:T.textSm,fontWeight:600}}>Add your first vehicle below to start</div>}
              {demoVehicleCapped&&showAddCapNote&&<div style={{fontSize:11,color:T.textMd,textAlign:"right",lineHeight:1.5,marginTop:2}}>You've used your free vehicle check. Your account is pending review. {goLiveSent?<span style={{color:T.teal,fontWeight:700}}>Request sent ✓</span>:<button onClick={()=>requestFullAccess().then(setGoLiveSent)} style={{background:"none",border:"none",padding:0,color:T.teal,fontWeight:700,textDecoration:"none",cursor:"pointer",font:"inherit"}}>Request faster review</button>} to add more vehicles and refresh sooner.</div>}
            </div>
          </div>
        </div>
        <div style={{background:T.slateL,borderTop:`1px solid ${T.border}`,padding:"5px 20px",display:"flex",flexWrap:"wrap",alignItems:"center",justifyContent:"flex-end",rowGap:6,gap:10}}>
          {/* Defaults to dark (theme.js's T + this file's theme state) until
              an account explicitly picks light, at which point changeTheme
              saves that choice to display_prefs and it persists from then on. */}
          <div style={{display:"flex",alignItems:"center",gap:2,background:T.card,borderRadius:14,padding:2,border:`1px solid ${T.border}`,marginRight:4}}>
            <button onClick={()=>changeTheme("light")} title="Light theme" style={{padding:"3px 8px",borderRadius:11,border:"none",fontSize:11,background:theme==="light"?T.slateL:"transparent",boxShadow:theme==="light"?T.shadow:"none"}}>☀️</button>
            <button onClick={()=>changeTheme("dark")} title="Dark theme" style={{padding:"3px 8px",borderRadius:11,border:"none",fontSize:11,background:theme==="dark"?T.slateL:"transparent",boxShadow:theme==="dark"?T.shadow:"none"}}>🌙</button>
          </div>
          {email&&<span className="hf-header-email" style={{fontSize:11,color:T.textSm}}>{email}</span>}
          {/* Shop opens the four-square deal worksheet — demo-gated the
              same way Add Vehicle is (client-side accessLevel check,
              same shared requestFullAccess() upgrade-prompt flow), since
              the worksheet's real value depends on live pricing and
              dealer search, not something worth showing a demo account a
              simulated version of the way the rest of this app does. */}
          <button onClick={()=>{if(accessLevel==="demo")setShowShopCapNote(v=>!v);else setActiveView(v=>v==="buying"?null:"buying");}} style={{fontSize:11,color:T.teal,background:"none",border:"none",cursor:"pointer",fontWeight:600,padding:0}}>{activeView==="buying"?"Back to Fleet":"Shop"}</button>
          {/* Routes through the exact same accessLevel!=="demo" gate as
              onBuying below — a resumable deal only ever gets set for a
              non-demo account in the first place (see the effect above),
              but this click handler re-checks it directly too rather
              than assuming that upstream gate alone is enough. Uses the
              conversation's own auto-generated title, not a make/model —
              nothing reads the deals table anywhere in this app yet
              (Known issue #33), so there's no clean vehicle identity to
              show here beyond what the conversation itself already has. */}
          {/* The id on resumableDeal used to go no further than this
              label -- the button navigated and nothing was restored. It
              is now handed to HomeFleetBuying, which rehydrates the
              thread and the sheet behind it. */}
          {resumableDeal&&activeView!=="buying"&&(
            <button onClick={()=>{if(accessLevel!=="demo")setActiveView("buying");}} style={{fontSize:11,color:T.teal,background:"none",border:"none",cursor:"pointer",fontWeight:600,padding:0}}>Resume: {resumableDeal.title||"your deal"}</button>
          )}
          {/* Client-side visibility only — the real gate is every
              /api/admin/* endpoint's own requireAdmin() check, which
              refuses a non-admin regardless of whether this link is
              ever rendered or clicked. */}
          <button onClick={()=>setActiveView(v=>v==="settings"?null:"settings")} style={{fontSize:11,color:T.teal,background:"none",border:"none",cursor:"pointer",fontWeight:600,padding:0}}>{activeView==="settings"?"Back to Fleet":"Settings"}</button>
          {accessLevel==="admin"&&<button onClick={()=>setActiveView(v=>v==="admin"?null:"admin")} style={{fontSize:11,color:T.teal,background:"none",border:"none",cursor:"pointer",fontWeight:600,padding:0}}>{activeView==="admin"?"Back to Fleet":"Admin"}</button>}
          <button onClick={handleSignOut} style={{fontSize:11,color:T.teal,background:"none",border:"none",cursor:"pointer",fontWeight:600,padding:0}}>Sign out</button>
        </div>
        {accessLevel==="demo"&&showShopCapNote&&(
          <div style={{background:T.amberL,borderTop:`1px solid ${T.amberBorderStrong}`,padding:"8px 20px",textAlign:"right",fontSize:11,color:T.amber,fontWeight:600,lineHeight:1.5}}>
            Shopping for a vehicle needs live pricing and dealer search — not available until your account is reviewed. {goLiveSent?<span style={{color:T.teal,fontWeight:700}}>Request sent ✓</span>:<button onClick={()=>requestFullAccess().then(setGoLiveSent)} style={{background:"none",border:"none",padding:0,color:T.teal,fontWeight:700,textDecoration:"none",cursor:"pointer",font:"inherit"}}>Request faster review</button>} to unlock it.
          </div>
        )}
      </div>

      {showWalkthrough?(
        <DemoWalkthrough onAddVehicle={v=>setVehicles(p=>[v,...p])} onComplete={()=>setShowWalkthrough(false)} customerName={fullName}/>
      ):activeView==="admin"?<AdminDashboard/>:activeView==="settings"?<AccountSettings/>:activeView==="buying"?(
        <>
          {/* One-time, dismissible -- never a hard wall on shopping.
              Shown whenever the buying flow opens with no zip on file and
              the household hasn't dismissed it before; gone for good once
              they dismiss it OR once a zip is actually on file, whichever
              happens first (the zip half of that is just !householdZip
              below -- no separate bookkeeping needed for it). Persisted
              in displayPrefs, the same per-user JSON blob theme/openTab
              already use (migrations/0020), rather than a new column --
              this is exactly the kind of small dismiss-state it already
              exists for. See HOMEFLEET_LOCATION_FALLBACK investigation,
              2026-09-15: with no zip on file, inventory searches silently
              default to a Dallas–Fort Worth radius, and until this pass
              nothing on screen said so before a household reached the
              listings themselves. */}
          {!householdZip&&!displayPrefs.zipNudgeDismissed&&(
            <div style={{maxWidth:980,margin:"12px auto 0",padding:"0 16px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap",background:T.amberL,border:`1px solid ${T.amberBorderStrong}`,borderRadius:10,padding:"10px 14px"}}>
                <div style={{fontSize:12.5,color:T.amber,fontWeight:600,lineHeight:1.5}}>No ZIP code on file — inventory searches default to the Dallas–Fort Worth area instead of listings near you.</div>
                <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                  <button onClick={()=>setActiveView("settings")} style={{fontSize:12,fontWeight:700,color:T.amber,background:"none",border:`1.5px solid ${T.amberBorderStrong}`,borderRadius:7,padding:"6px 12px",cursor:"pointer"}}>Add ZIP</button>
                  <button onClick={()=>savePrefs({...displayPrefs,zipNudgeDismissed:true})} style={{fontSize:12,fontWeight:700,color:T.amber,background:"none",border:"none",cursor:"pointer",padding:0}}>Dismiss</button>
                </div>
              </div>
            </div>
          )}
          <HomeFleetBuying vehicles={vehicles} restoredIntent={restoredIntent} resumableConversation={resumableDeal} accessLevel={accessLevel} householdState={householdState} householdZip={householdZip} pendingHandoff={buyingSeedIntent} onVehicleAdded={v=>setVehicles(p=>[v,...p])} onVehicleRemoved={id=>setVehicles(p=>p.filter(x=>x.id!==id))} onOpenGeneralChat={()=>setClydeOpen(true)}/>
        </>
      ):(
      <div style={{maxWidth:980,margin:"0 auto",padding:"20px 16px 52px"}}>
        <div className="fade-up" style={{background:T.card,borderRadius:16,border:`1px solid ${T.border}`,boxShadow:T.shadow,padding:"18px 20px",marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}><div style={{fontSize:15,fontWeight:800,color:T.text}}>Portfolio Overview</div><span style={{fontSize:12,color:T.textSm}}>{vehicles.length} vehicle{vehicles.length!==1?"s":""}</span></div>
          <div className="hf-portfolio-line">
            {[{l:"Fleet Value",v:fmt(totals.value)},{l:"Total Financed",v:fmt(totals.loan)},{l:"Equity Position",v:fmt(totals.equity),c:totals.equity>=0?T.green:T.red},{l:"Total Payments",v:fmt(totals.payments)+"/mo"},{l:"Monthly Outlay",v:fmt(totals.monthlyOutlay)+"/mo"}].map(m=>(
              <div key={m.l} style={{display:"flex",flexDirection:"column",gap:6}}>
                <span style={{fontSize:11,fontWeight:600,color:T.textSm,textTransform:"uppercase",letterSpacing:".06em"}}>{m.l}</span>
                <span className="mono" style={{fontSize:18,fontWeight:700,color:m.c||T.text,lineHeight:1}}>{m.v}</span>
              </div>
            ))}
          </div>
          <div className="hf-portfolio-tabs">
            {PORTFOLIO_TABS.map(t=>{
              const amt=t.key==="insurance"?totals.insSave:t.key==="refi"?totals.refiSave:totals.offerValue;
              return(
                <button key={t.key} className={`hf-ptab${activePTab===t.key?" active":""}`} onClick={()=>setActivePTab(p=>p===t.key?null:t.key)}>
                  <span>{t.label}</span>
                  <span className="mono" style={{fontSize:15,fontWeight:800,color:activePTab===t.key?T.teal:(amt>0?T.green:T.textSm)}}>{t.key==="offer"?fmt(amt):(amt>0?"+"+fmt(amt)+"/mo":"—")}</span>
                </button>
              );
            })}
          </div>
          {activePTab&&(
            <div className="hf-pbreakdown">
              {vehicles.map(v=>{
                const val=resolveValue(v);
                let right;
                if(activePTab==="insurance"){
                  const q=resolveInsuranceQuote(v);const save=q?Math.max(0,num(v.insurance)-q.amount):0;
                  right=<span className="mono" style={{fontWeight:700,color:save>0?T.green:T.textSm}}>{save>0?"+"+fmt(save)+"/mo":"Already best rate"}</span>;
                }else if(activePTab==="refi"){
                  const hasLoan=num(v.loan)>0;
                  const save=hasLoan&&num(v.rate)?Math.max(0,Math.round((num(v.loan)*((num(v.rate)-resolveRefiRate(v).amount)/100))/12)):0;
                  right=<span className="mono" style={{fontWeight:700,color:save>0?T.green:T.textSm}}>{!hasLoan?"No loan":save>0?"+"+fmt(save)+"/mo":"Rate already competitive"}</span>;
                }else{
                  const o=resolveBestOffer(v,val.amount);
                  right=o?<span className="mono" style={{fontWeight:700,color:T.teal}}>{fmt(o.amount)} <span style={{color:T.textSm,fontWeight:500}}>net {fmt(o.amount-num(v.loan))}</span></span>:<span className="mono" style={{color:T.textSm}}>—</span>;
                }
                return(
                  <div key={v.id} className="hf-pbreakdown-row">
                    <span style={{color:T.text,fontWeight:600}}>{v.nickname}<span style={{color:T.textSm,fontSize:11,fontWeight:500,marginLeft:6}}>{v.year} {v.make} {v.model}</span></span>
                    {right}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {vehicles.map((v,i)=><VehicleRow key={v.id} vehicle={v} maintenance={maintenance.filter(m=>m.vehicleId===v.id)} idx={i} onUpdate={updateVehicle} onDelete={deleteVehicle} mode={dataMode} onServiceSchedule={setSvcVehicle} onEnrich={mergeFinancials} householdState={householdState} onTcoChange={handleTcoChange} initialTab={displayPrefs.openTab?.[v.id]??null} onTabChange={handleTabChange}/>)}
          {vehicles.length===0&&<div style={{textAlign:"center",padding:"60px 20px",background:T.card,borderRadius:16,border:`1px solid ${T.border}`}}><div style={{fontSize:40,marginBottom:14}}>🚗</div><div style={{fontSize:18,fontWeight:800,color:T.text,marginBottom:8}}>No vehicles yet</div><div style={{fontSize:14,color:T.textMd,marginBottom:20}}>Add your first vehicle to see your full financial picture.</div><button onClick={()=>setAddVeh(true)} style={{background:`linear-gradient(135deg,${T.teal},#0f766e)`,color:"#fff",border:"none",borderRadius:10,padding:"12px 28px",fontSize:14,fontWeight:700}}>+ Add Your First Vehicle</button></div>}
        </div>

        <div style={{marginTop:28,textAlign:"center",fontSize:12,color:T.textSm,lineHeight:1.8}}>
          HomeFleet — Phase 2a — {isLive?"Full access active":"Account pending review — limited features"}
        </div>
      </div>
      )}

      {addVeh&&<VehicleModal mode={dataMode} customerName={fullName} onSave={async v=>{if(v){const ok=await addVehicle(v);if(ok)setAddVeh(false);}else{setAddVeh(false);}}} onClose={()=>setAddVeh(false)}/>}
      <ClydePanel vehicles={vehicles} mode={dataMode} accessLevel={accessLevel} onSchedule={(v)=>setSvcVehicle(v||vehicles[0])} onBuying={(seedIntent)=>{if(accessLevel!=="demo"){setBuyingSeedIntent(seedIntent||null);setActiveView("buying");}}} open={clydeOpen} onOpenChange={setClydeOpen} hideTrigger={activeView==="buying"}/>
      {svcVehicle&&(
        <div style={{position:"fixed",inset:0,zIndex:950,display:"flex",alignItems:"stretch",justifyContent:"flex-end"}}>
          <div onClick={()=>setSvcVehicle(null)} style={{position:"absolute",inset:0,background:"rgba(15,23,42,0.55)",backdropFilter:"blur(4px)"}}/>
          <div className="fade-up" style={{position:"relative",width:"min(480px,100vw)",background:T.card,boxShadow:"-8px 0 40px rgba(0,0,0,0.3)",display:"flex",flexDirection:"column",zIndex:951}}>
            <ServiceModal vehicles={vehicles} initialVehicle={svcVehicle} onClose={()=>setSvcVehicle(null)}/>
          </div>
        </div>
      )}
    </div>
  );
}
